import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

/* Provides the latest /admin/system/health-deep result to any component that
   needs to gate destructive / credit-burning actions.

   Components anywhere under <SystemHealthProvider> can call useSystemHealth()
   to read { overall, checks, criticalIssues, refresh }. The SystemHealthPill
   in StoryboardPanel.jsx triggers a refresh whenever the admin clicks
   "Re-run check", and other components subscribe via the context.

   On initial mount: one auto-ping. After that: only refresh on user-triggered
   re-runs (avoid spamming the endpoint, which would cost a Mongo ping +
   GridFS query per call).
*/

const SystemHealthContext = createContext({
  overall: null,
  checks: {},
  criticalIssues: [],
  refresh: () => {},
  loading: false,
});

const CRITICAL_KEYS = ["ffmpeg", "mongo", "gridfs", "emergent_key"];

export function SystemHealthProvider({ children, enabled = true }) {
  const [state, setState] = useState({ overall: null, checks: {}, loading: false });

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/admin/system/health-deep");
      setState({ overall: data.overall, checks: data.checks || {}, loading: false });
    } catch {
      setState({ overall: "degraded", checks: {}, loading: false });
    }
  }, [enabled]);

  useEffect(() => { if (enabled) refresh(); }, [enabled, refresh]);

  const criticalIssues = CRITICAL_KEYS
    .filter((k) => state.checks?.[k]?.status && !["ok", "present"].includes(state.checks[k].status))
    .map((k) => ({
      key: k,
      label: { ffmpeg: "ffmpeg", mongo: "MongoDB", gridfs: "GridFS",
               emergent_key: "Emergent LLM key" }[k],
      status: state.checks[k].status,
      fix: state.checks[k].fix,
    }));

  return (
    <SystemHealthContext.Provider value={{ ...state, criticalIssues, refresh }}>
      {children}
    </SystemHealthContext.Provider>
  );
}

export function useSystemHealth() {
  return useContext(SystemHealthContext);
}

/* Helper for components that only want the boolean. */
export function useEnvDegraded() {
  const { overall, criticalIssues } = useSystemHealth();
  return {
    degraded: overall === "degraded" && criticalIssues.length > 0,
    issues: criticalIssues,
    reason: criticalIssues.length
      ? criticalIssues.map((i) => `${i.label}: ${i.status}`).join(" · ")
      : "",
  };
}
